import { errorMessageFrom } from "../../../../../utils/formErrors";
import { ensureLegalUnits } from "../../../../../services/useOrgData";
import React, { useEffect, useState } from "react";
import {
  Box, Stack, Dialog, TextField, CircularProgress, IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import EmptyState from "../../../../../component/EmptyState";
import SectionLoader from "../../../../../component/SectionLoader";
import { useNotify } from "../../../../../component/NotificationProvider";
import { addLegalUnit, updateLegalUnit } from "../../../../../services/auth";
import { getActiveLegalUnit, setActiveLegalUnit } from "../../../../../services/globalData";
import { useActiveLegalUnit } from "../../../../../services/useOrgData";
import { tokens, displayFont, radius } from "../../../../../theme";

import InlineMessage from "../../../../../component/InlineMessage";
import { countryList } from "../../../../../component/CountryList";
import { currencyForCountry, currencySymbol } from "../../../../../utils/currency";
import { timezoneForCountry, TIMEZONES } from "../../../../../utils/timezone";
import { Autocomplete } from "@mui/material";
const cardSx = { background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px` };

const EntityDialog = ({ open, onClose, editing, onSaved }) => {
  const notify = useNotify();
  const [form, setForm] = useState({ name: "", address: "", country: "", currency: "", timezone: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: editing?.name || "",
        address: editing?.address || "",
        country: editing?.country || "",
        currency: editing?.currency || "",
        timezone: editing?.timezone || "",
      });
      setErrors({});
    }
  }, [open, editing]);

  const change = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: "" }));
  };

  // Picking a country fills the currency in, and only when it has not been set
  // deliberately. An entity may be registered in one place and pay in another —
  // that is why the two are separate fields — so a choice already made is never
  // overwritten by a later change of country.
  const chooseCountry = (label) => {
    setForm((p) => ({
      ...p,
      country: label || "",
      currency: p.currency || (label ? currencyForCountry(label) : ""),
      // Same rule as currency: filled in from the country, never overwriting a
      // choice already made. The map guesses one zone per country, which is
      // wrong for the US and Australia by construction — hence a field.
      timezone: p.timezone || (label ? timezoneForCountry(label) : ""),
    }));
    setErrors((p) => ({ ...p, country: "" }));
  };

  const save = async () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.address.trim()) e.address = "Address is required";
    // ISO 4217 is three letters. money.js looks the exponent up by exact code,
    // so anything else silently falls back to two decimal places — right for
    // INR, wrong for JPY and KWD.
    if (form.currency && !/^[A-Z]{3}$/.test(form.currency.trim()))
      e.currency = "A currency is a three-letter code, like INR or SGD";
    // The server refuses a zone it cannot resolve, so catch it here rather than
    // letting a save fail. Checked by asking the browser, which uses the same
    // IANA database Node does.
    if (form.timezone && !TIMEZONES.includes(form.timezone.trim())) {
      try {
        new Intl.DateTimeFormat("en-GB", { timeZone: form.timezone.trim() });
      } catch {
        e.timezone = "That is not a timezone name. Pick one from the list.";
      }
    }
    setErrors(e);
    if (Object.keys(e).length) return;

    setSaving(true);
    try {
      const res = editing
        ? await updateLegalUnit(editing._id, form)
        : await addLegalUnit(form);
      if (res?.success) {
        notify.success(editing ? "Legal entity updated" : "Legal entity added");
        onSaved();
        onClose();
      } else {
        notify.error(errorMessageFrom(res, "Could not save legal entity"));
      }
    } catch (err) {
      notify.error(err?.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: "12px", width: "min(30rem,90vw)" } }}>
      <Box sx={{ p: "1.5rem" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb="1.25rem">
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>
            {editing ? "Edit legal entity" : "Add legal entity"}
          </Box>
          <IconButton aria-label="Close" size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Stack>
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "0.5rem" }}>Legal company name *</Box>
        <TextField fullWidth size="small" value={form.name} placeholder="e.g. Acme India Pvt Ltd"
          onChange={(e) => change("name", e.target.value)} error={!!errors.name} helperText={errors.name} sx={{ mb: "1rem" }} />
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "0.5rem" }}>Address *</Box>
        <TextField fullWidth size="small" value={form.address} placeholder="Registered address"
          onChange={(e) => change("address", e.target.value)} error={!!errors.address} helperText={errors.address} sx={{ mb: "1rem" }} />

        {/* The reason an organisation has more than one entity is that each is
            registered somewhere different — so this is where the currency its
            payroll runs in is decided. Left blank, the entity falls back to the
            organisation, which is what every entity did before the field
            existed. */}
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "0.5rem" }}>Country of registration</Box>
        <Autocomplete
          options={countryList}
          value={form.country || null}
          onChange={(_e, v) => chooseCountry(v)}
          renderInput={(params) => (
            <TextField {...params} size="small" placeholder="e.g. India" />
          )}
          sx={{ mb: "1rem" }}
        />

        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "0.5rem" }}>Payroll currency</Box>
        <TextField fullWidth size="small" value={form.currency}
          placeholder="Follows the organisation if left blank"
          onChange={(e) => change("currency", e.target.value.toUpperCase())}
          error={!!errors.currency} helperText={errors.currency}
          InputProps={{
            endAdornment: form.currency ? (
              <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, pl: "0.5rem" }}>
                {currencySymbol(form.currency)}
              </Box>
            ) : null,
          }}
          sx={{ mb: "1rem" }} />

        {/* Attendance is measured in this zone: a shift that starts at 08:00
            means 08:00 here, and a punch belongs to the day it is here. Without
            it the server's own clock decides, which put every Indian arrival
            5½ hours early. */}
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "0.5rem" }}>
          Timezone for attendance
        </Box>
        <Autocomplete
          options={TIMEZONES}
          value={form.timezone || null}
          onChange={(_e, v) => change("timezone", v || "")}
          renderInput={(params) => (
            <TextField {...params} size="small" placeholder="e.g. Asia/Kolkata"
              error={!!errors.timezone} helperText={errors.timezone} />
          )}
          sx={{ mb: "1.5rem" }}
        />
        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <BtnSecondary onClick={onClose} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
          <BtnPrimary onClick={save} disabled={saving} sx={{ borderRadius: "8px" }}>
            {saving ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : (editing ? "Save" : "Add")}
          </BtnPrimary>
        </Stack>
      </Box>
    </Dialog>
  );
};

const LegalEntities = () => {
  const notify = useNotify();
  const active = useActiveLegalUnit();
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetch = async () => {
    setLoading(true);
    setError(false);
    try {
      // Writes here invalidate the cached list, so this still reaches the
      // server after an add or a rename — and populates the cache the entity
      // switcher reads, rather than keeping a second copy in step by hand.
      const list = (await ensureLegalUnits()) || [];
      setEntities(list);
    } catch (err) {
      setError(true);
      notify.error(err?.message || "Failed to load legal entities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, []);

  const makeActive = (entity) => {
    setActiveLegalUnit(entity);
    notify.success(`Now working in ${entity.name}`);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb="1.25rem">
        <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Legal Entities</Box>
        <BtnPrimary onClick={() => { setEditing(null); setDialogOpen(true); }}
          startIcon={null} sx={{ height: "2.75rem", px: "1.25rem", borderRadius: "8px" }}>
          <AddIcon sx={{ fontSize: "1.1rem", mr: 0.5 }} /> Add Legal Entity
        </BtnPrimary>
      </Stack>

      {loading ? (
        <SectionLoader />
      ) : error ? (
        <EmptyState variant="error" title="Couldn't load legal entities" onRetry={fetch} />
      ) : entities.length === 0 ? (
        <EmptyState title="No legal entities yet" description="Add your first legal entity to get started." />
      ) : (
        <Stack spacing={1.25}>
          {entities.map((entity) => {
            const isActive = entity._id === active?._id;
            return (
              <Stack key={entity._id} direction="row" alignItems="center" justifyContent="space-between"
                sx={{ ...cardSx, p: "1rem 1.25rem" }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                  <Box sx={{ width: "2.5rem", height: "2.5rem", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", background: `${tokens.accentTint}`, color: tokens.accent, flexShrink: 0 }}>
                    <ApartmentOutlinedIcon />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>{entity.name}</Box>
                      {isActive && (
                        <InlineMessage tone="success" sx={{ fontWeight: 600, px: 1, py: 0.5 }}>
            Active
          </InlineMessage>
                      )}
                    </Stack>
                    <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {/* Where it is registered and what it pays in, on the row
                          rather than behind the edit dialog: these are what
                          distinguish one entity from another, and an admin
                          choosing which to work in is choosing between them. */}
                      {[entity.address, entity.country, entity.currency, entity.timezone].filter(Boolean).join(" · ")}
                    </Box>
                  </Box>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {!isActive && (
                    <BtnSecondary onClick={() => makeActive(entity)} sx={{ borderRadius: "8px", fontSize: "0.8125rem" }}>
                      Set active
                    </BtnSecondary>
                  )}
                  <IconButton aria-label="Edit" size="small" onClick={() => { setEditing(entity); setDialogOpen(true); }} sx={{ color: tokens.textMuted }}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      )}

      <EntityDialog open={dialogOpen} onClose={() => setDialogOpen(false)} editing={editing} onSaved={fetch} />
    </Box>
  );
};

export default LegalEntities;
